// Пересчёт всех ведомостей периода и разбор того, почему остались нули.
//
// ## Зачем эта панель появилась
//
// 16.09.2026 в 14:21 финансовый директор заказчика нажал «Создать ведомости на период» за июль:
// 77 ведомостей, 366 человек, 371 строка — и реестр показал нули во всех. Это было последнее
// его действие в программе: дальше он ушёл спрашивать в переписке, перенесли ли мы ведомости.
//
// Нули были честные. Создание ведомости денег не считает намеренно: часы приходят из табеля, и
// снимаются они отдельным пересчётом. И программа даже советовала, что делать: «откройте
// ведомость и нажмите „Пересчитать“». Совет был верным для ОДНОЙ ведомости и невыполнимым для
// семидесяти семи — и человек, прочитав его, не сделал ничего. Молчание тут ни при чём:
// продукт просил семьдесят семь одинаковых заходов и считал это нормальным.
//
// ## Главное здесь — не кнопка, а список причин
//
// Замер того же дня: пересчёт июля прямо сейчас оживил бы 18 строк из 371. Остальные 353
// остались бы нулями по трём разным причинам. Значит «пересчитано 77» человеку не отвечает
// ни на что — ему нужно «чего не хватает и скольким», и по каждой причине следующий шаг.
//
// Поэтому после пересчёта панель показывает свод: блокирующие первыми, у каждой сказано,
// скольких людей она держит, и написан глагол — «заполнить тариф», «открыть табель».
//
// ## Ключа операции здесь нет, и это решение
//
// Пересчёт денежных записей не рождает: он обновляет мнение о суммах по нынешним данным.
// Повтор безвреден и осмыслен — человек заполнил тариф и жмёт снова. Ключ, наоборот, вернул бы
// ему ПРЕЖНИЙ отчёт на второе нажатие, то есть соврал бы про его же правку.

import { useState } from 'react';

import { RiAlertLine, RiCheckboxCircleLine, RiRefreshLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { RecalcPeriodOut } from '@/shared/api/endpoints/payroll';
import type { ApiError } from '@/shared/errors/api-error';
import { useAuthStore } from '@/shared/auth/store';
import { formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatPeriodTitle, parsePeriod } from '@/shared/time/period';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { Skeleton } from '@/shared/ui/screen-state';

import {
  FILTER_MONEY,
  FILTER_PERIOD,
  FILTER_SHEET,
  MONEY_EMPTY,
  PANEL_SHEET_TARIFF,
  VIEW_SHEETS,
} from '../../route';

import { useRecalcPeriod, useRegistry } from '../queries';

const EDIT_PERMISSION = 'core.payroll.edit';

export function RecalcPeriodPanel({ id }: { id: string }) {
  const права = useAuthStore((state) => state.permissions);
  const можноПравить = can(права, EDIT_PERMISSION);
  const период = parsePeriod(id);
  const реестр = useRegistry(период);
  const пересчёт = useRecalcPeriod();
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [итог, setИтог] = useState<RecalcPeriodOut | null>(null);

  const ведомости = реестр.data?.items ?? [];
  // Считаем только те, что правятся: утверждённые и закрытые пересчёт не трогает, и обещать
  // человеку семьдесят семь там, где пересчитается сорок, нельзя.
  const правятся = ведомости.filter((в) => в.can_edit).length;

  return (
    <div className='flex flex-col gap-4 p-5'>
      <p className='max-w-prose text-paragraph-sm text-text-sub-600'>
        Деньги в ведомости появляются не от её создания, а от пересчёта: часы приходят из табеля
        объекта. Здесь пересчитываются все ведомости {formatPeriodTitle(период)} сразу, и внизу
        написано, чего не хватило.
      </p>

      {реестр.isLoading ? (
        <Skeleton className='h-16 w-full' />
      ) : (
        <div className='flex flex-col gap-1 text-paragraph-sm text-text-strong-950'>
          <span>
            Ведомостей за месяц: {ведомости.length}, из них пересчитаются {правятся}
          </span>
          {ведомости.length > правятся && (
            <span className='text-paragraph-xs text-text-sub-600'>
              Остальные утверждены, выплачены или закрыты: их суммы зафиксированы, и пересчёт их не
              трогает.
            </span>
          )}
        </div>
      )}

      <div>
        <Button.Root
          type='button'
          variant='primary'
          mode='filled'
          size='small'
          onClick={() => {
            if (!можноПравить) {
              setЗапрет('Пересчёт делает финансовый отдел или администратор.');
              return;
            }
            if (ведомости.length === 0) {
              setЗапрет('За этот месяц ведомостей нет. Сначала создайте их на период.');
              return;
            }
            setЗапрет(null);
            пересчёт.mutate(
              { year: период.year, month: период.month },
              { onSuccess: (ответ) => setИтог(ответ) },
            );
          }}
        >
          <Button.Icon as={RiRefreshLine} />
          {пересчёт.isPending ? 'Считаем…' : 'Пересчитать весь период'}
        </Button.Root>
      </div>

      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {пересчёт.error !== null && <ErrorAlert error={пересчёт.error as ApiError} />}

      {итог !== null && <Отчёт итог={итог} весьМесяц />}
    </div>
  );
}

/**
 * Что вышло. Сначала деньги, потом причины, и только потом перечень объектов.
 *
 * Порядок не косметический: человек нажал эту кнопку, чтобы появились деньги, и первым он
 * обязан увидеть ответ на свой вопрос. «Пересчитано 77» — это про нашу работу, а не про его.
 */
export function Отчёт({
  итог,
  объект,
  весьМесяц = false,
}: {
  итог: RecalcPeriodOut;
  /**
   * Пересчитан весь месяц, а не несколько объектов.
   *
   * Только тогда стоит кнопка «Показать все N в списке ведомостей»: она открывает список
   * ведомостей месяца без денег. Окно «Создать ведомость» пересчитывает лишь заведённые объекты,
   * и тот же список показал бы ещё и чужие для этого отчёта ведомости — число на кнопке
   * разошлось бы с числом над списком.
   */
  весьМесяц?: boolean;
  /**
   * Пересчитан ОДИН объект — ведомость, заведённая под сегодняшнюю выплату. Тогда переход
   * «Заполнить тарифы» открывает лист, суженный до этого объекта: человеку нужны его люди, а
   * не триста строк месяца.
   */
  объект?: string;
}) {
  const проблемные = итог.sheets.filter(осталисьНули);
  // Причина нуля — словами сервера из реестра месяца, теми же, что стоят в строке списка слева:
  // одна ведомость не называется в двух местах по-разному. Реестр уже в кэше (его держит экран,
  // а пересчёт его обновляет), второго запроса за ним нет.
  const реестр = useRegistry({ year: итог.year, month: итог.month });
  const причины = new Map(
    (реестр.data?.items ?? []).map((ведомость) => [ведомость.id, ведомость.zero_reason ?? null]),
  );
  const периодВАдресе = `${итог.year}-${String(итог.month).padStart(2, '0')}`;
  // Полный список — это список ведомостей слева, суженный до тех, где денег нет: у каждой там
  // написано, чего не хватает, а нажатие открывает людей. Адрес без панели, поэтому переход
  // закрывает это окно. Тот же отбор делает переключатель «Без денег» над списком.
  const списокБезДенег = `/payroll/${VIEW_SHEETS}?f.${FILTER_PERIOD}=${периодВАдресе}&f.${FILTER_MONEY}=${MONEY_EMPTY}`;
  return (
    <div className='flex flex-col gap-4 border-t border-stroke-soft-200 pt-4'>
      <div className='flex flex-col gap-1'>
        <span className='text-label-lg tabular-nums text-text-strong-950'>
          {formatRubles(итог.accrued)} начислено
        </span>
        <span className='text-paragraph-sm text-text-sub-600'>
          {итог.sheets_with_money > 0
            ? `Деньги появились на ${countLabel(итог.sheets_with_money, ['объекте', 'объектах', 'объектах'])} из ${итог.recalculated}`
            : 'Денег не появилось ни на одном объекте: данных пока не хватает'}
          {итог.rate_pulled > 0 &&
            `. Тариф дозаполнен из назначений у ${countLabel(итог.rate_pulled, ['строки', 'строк', 'строк'])}`}
          {(итог.rate_refreshed ?? 0) > 0 &&
            `. Тариф обновлён по изменению тарифа у ${countLabel(итог.rate_refreshed ?? 0, ['строки', 'строк', 'строк'])}`}
        </span>
      </div>

      {итог.reasons.length > 0 ? (
        <div className='flex flex-col gap-2'>
          <span className='text-label-sm text-text-strong-950'>Чего не хватает</span>
          <ul className='flex flex-col gap-2'>
            {итог.reasons.map((причина) => (
              <li key={причина.code} className='flex flex-col gap-0.5 rounded-lg bg-bg-weak-50 p-3'>
                <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
                  {причина.blocking && (
                    <RiAlertLine className='size-4 shrink-0 text-error-base' aria-hidden />
                  )}
                  {countLabel(причина.people, ['человек', 'человека', 'человек'])} —{' '}
                  {причина.next_action}
                </span>
                <span className='max-w-prose text-paragraph-xs text-text-sub-600'>
                  {причина.text}
                </span>
                {/* Причина без пути к действию — это жалоба. Самая частая («нет тарифа»,
                    312 человек у заказчика) ведёт прямо в лист, где это и заполняется. */}
                {причина.code === 'rate_missing' && (
                  <LinkButton.Root variant='primary' size='small' underline asChild>
                    <Link
                      to={`/payroll/${VIEW_SHEETS}?f.${FILTER_PERIOD}=${периодВАдресе}&p=${PANEL_SHEET_TARIFF}:${периодВАдресе}${объект === undefined ? '' : `~${объект}`}`}
                    >
                      Заполнить тарифы
                    </Link>
                  </LinkButton.Root>
                )}
              </li>
            ))}
          </ul>
          {итог.blocking_employees > 0 && (
            <span className='text-paragraph-xs text-text-sub-600'>
              Пока это не разобрано, утвердить ведомости не получится:{' '}
              {countLabel(итог.blocking_employees, [
                'человек мешает',
                'человека мешают',
                'человек мешают',
              ])}
              .
            </span>
          )}
        </div>
      ) : (
        <span className='flex items-center gap-1.5 text-paragraph-sm text-text-sub-600'>
          <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' aria-hidden />
          Замечаний нет: всё, что можно было посчитать, посчитано.
        </span>
      )}

      {проблемные.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-label-sm text-text-strong-950'>
            Объекты, где остались нули: {проблемные.length}
          </span>
          {/* Название — ссылка на саму ведомость: открывается она в том же списке «без денег»,
              что и по кнопке ниже, и следующую можно взять прямо оттуда, не возвращаясь сюда. */}
          <ul className='flex flex-col gap-1 text-paragraph-xs text-text-sub-600'>
            {проблемные.slice(0, 12).map((лист) => (
              <li key={лист.payroll_id} className='flex flex-wrap items-baseline gap-x-2'>
                <Link
                  to={`${списокБезДенег}&f.${FILTER_SHEET}=${лист.payroll_id}`}
                  className='text-text-strong-950 underline decoration-stroke-sub-300 underline-offset-4 transition duration-200 ease-out hover:decoration-current'
                >
                  {лист.site_name}
                </Link>
                <span>{чегоНеХватает(лист, причины.get(лист.payroll_id) ?? null)}</span>
              </li>
            ))}
          </ul>
          {проблемные.length > 12 && (
            <span className='text-paragraph-xs text-text-sub-600'>
              и ещё {проблемные.length - 12}
            </span>
          )}
          {/* До 11.10.2026 здесь стояло «Полный список — в реестре ниже»: ниже не было ничего,
              а список ведомостей стоит слева и показывает все, а не эти. Заказчик спросил, какой
              реестр и где его найти. Теперь это кнопка, которая этот список и открывает. */}
          {весьМесяц && (
            <>
              <div>
                <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
                  {/* Одна ведомость — она сразу и открывается: «показать все 1» звучит странно,
                      а искать единственную строку в списке незачем. */}
                  <Link
                    to={
                      проблемные.length === 1
                        ? `${списокБезДенег}&f.${FILTER_SHEET}=${проблемные[0]?.payroll_id ?? ''}`
                        : списокБезДенег
                    }
                  >
                    {проблемные.length === 1
                      ? 'Открыть эту ведомость'
                      : `Показать все ${проблемные.length} в списке ведомостей`}
                  </Link>
                </Button.Root>
              </div>
              <span className='max-w-prose text-paragraph-xs text-text-sub-600'>
                Окно закроется, и в списке ведомостей останутся только те, где нет денег. У каждой в
                строке написано, чего не хватает, а внутри ведомости сказано подробнее.
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Ведомость, которую пересчитали, а денег в ней так и нет.
 *
 * Пропущенные (утверждённые, выплаченные, закрытые) сюда не входят: суммы у них зафиксированы,
 * и про них уже сказано строкой «Остальные утверждены…». До 11.10.2026 они попадали в этот
 * перечень с нулём, которого в них нет, — у пропущенной ведомости сумма в отчёте просто не
 * заполняется.
 */
function осталисьНули(лист: RecalcPeriodOut['sheets'][number]): boolean {
  return лист.recalculated && лист.total_payout === 0;
}

/**
 * Чего не хватает ведомости — словами строки списка, а не своими.
 *
 * До 11.10.2026 тут стояло «разобраться по 0» у ведомостей, где людей нет вовсе: пересчитывать
 * в них нечего, и разбирать тоже. Причина берётся фразой сервера — той же, что в строке списка
 * («табеля за этот месяц нет вовсе…», «нет тарифа: 3 строки»), чтобы одна ведомость не
 * называлась в двух местах по-разному. Своими словами — только когда сервер причины не назвал.
 */
function чегоНеХватает(
  лист: RecalcPeriodOut['sheets'][number],
  причинаСервера: string | null,
): string {
  if (лист.employees === 0) {
    return 'никого не назначили, считать некого';
  }
  if (причинаСервера !== null) {
    return причинаСервера;
  }
  if (лист.blocking_employees > 0) {
    return `у ${лист.blocking_employees} не посчиталось`;
  }
  return 'к выплате ноль, причины внутри ведомости';
}
